import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { planzone, WeeklyCalendar } from "@planzone/dto";
import {
  useAuthentication,
  useRetrievePersonalWeeklyCalendar,
  useEditPersonalWeeklyCalendar,
  useRetrieveCalendarsHolidays,
} from "@planzone/view-models";
import { CalendarHolidays } from "./calendarHolidays";
import { Button } from "@common/button";
import { AllocationUnit } from "@common/allocationUnit";
import { Loader } from "./loader";
import { Props } from "./types";
import styles from "./styles.scss";

function Calendar({ readOnly }: Props) {
  const { t } = useTranslation();
  const { calendar } = useRetrievePersonalWeeklyCalendar();
  const [form, setForm] = React.useState(calendar);
  const {
    isRequestPending,
    isRequestFailure,
    isRequestSuccess,
    retrievePersonalWeeklyCalendar,
  } = useRetrievePersonalWeeklyCalendar();
  const { isRequestPending: isEditRequestPending, editPersonalWeeklyCalendar } =
    useEditPersonalWeeklyCalendar();
  const { account } = useAuthentication();
  const {
    calendarsHolidays,
    isRequestFailure: isRetrieveCalendarsHolidaysFailure,
    isRequestPending: isRetrieveCalendarsHolidaysPending,
    isRequestSuccess: isRetrieveCalendarHolidaysSuccess,
    retrieveCalendarHolidays,
  } = useRetrieveCalendarsHolidays();

  const dayStatus: { id: number; day: string; capacity: number }[] = [
    { id: 1, day: "planning.monday", capacity: form.monday.capacity },
    { id: 2, day: "planning.tuesday", capacity: form.tuesday.capacity },
    { id: 3, day: "planning.wednesday", capacity: form.wednesday.capacity },
    { id: 4, day: "planning.thursday", capacity: form.thursday.capacity },
    { id: 5, day: "planning.friday", capacity: form.friday.capacity },
    { id: 6, day: "planning.saturday", capacity: form.saturday.capacity },
    { id: 7, day: "planning.sunday", capacity: form.sunday.capacity },
  ];

  const handleChange = (nextForm: Partial<WeeklyCalendar>) => {
    setForm({ ...form, ...nextForm });
  };

  const sendData = () => {
    editPersonalWeeklyCalendar({ account, calendar, nextCalendar: form });
  };

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      !isEditRequestPending &&
      retrievePersonalWeeklyCalendar(account);
  });

  React.useEffect(() => {
    !isRetrieveCalendarsHolidaysFailure &&
      !isRetrieveCalendarsHolidaysPending &&
      !isRetrieveCalendarHolidaysSuccess &&
      retrieveCalendarHolidays(account.planzone);
  });

  React.useEffect(() => {
    setForm(calendar);
  }, [calendar]);

  function updateCalendar(day: number, target: any): void {
    if (day === 1) {
      handleChange({ monday: { capacity: +target.value } });
    } else if (day === 2) {
      handleChange({ tuesday: { capacity: +target.value } });
    } else if (day === 3) {
      handleChange({ wednesday: { capacity: +target.value } });
    } else if (day === 4) {
      handleChange({ thursday: { capacity: +target.value } });
    } else if (day === 5) {
      handleChange({ friday: { capacity: +target.value } });
    } else if (day === 6) {
      handleChange({ saturday: { capacity: +target.value } });
    } else {
      handleChange({ sunday: { capacity: +target.value } });
    }
  }

  return (
    <div className={styles.calendar}>
      {isRequestPending || isRetrieveCalendarsHolidaysPending ? (
        <Loader />
      ) : (
        <div className={styles.calendarPreference}>
          <div className={styles.calendarHolidays}>
            <p className={styles.description}>
              {t("preferences.calendarHolidays.description")}
            </p>
            <CalendarHolidays planzone={planzone} resource={account.resource} />
          </div>

          <div className={styles.caseCalendar}>
            <h2 className={styles.hideElement}>hide</h2>
            <p>
              <AllocationUnit format="text" amount={2} />
            </p>
          </div>
          {dayStatus.map((day) => {
            return (
              <div className={styles.caseCalendar}>
                <h2>{t(day.day)}</h2>
                <form
                  className={classNames(styles.formData, {
                    [styles.readOnly]: readOnly,
                  })}
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendData();
                  }}
                >
                  <input
                    type="number"
                    className={styles.inputTime}
                    min="0"
                    max="24"
                    disabled={readOnly}
                    value={day.capacity}
                    onChange={({ target }) => updateCalendar(day.id, target)}
                  />
                </form>
              </div>
            );
          })}
        </div>
      )}
      <div className={styles.displayButton}>
        <Button
          type="submit"
          status={isEditRequestPending ? "pending" : "none"}
          press={sendData}
        >
          {t("taskEdition.submit")}
        </Button>
      </div>
    </div>
  );
}

export { Calendar };
